import type { Country, LoanProgram, MortgageTermsData, PaymentFrequency } from '../types/mortgageTypes';

export type QuickStartPreset = 'first-time' | 'refinance' | 'investment' | 'custom';

export interface PresetTabConfig {
  title: string;
  mortgageTerms: Partial<MortgageTermsData>;
}

export interface PresetNumbers {
  homePrice: number;
  downPaymentAmount: number;
  downPaymentPercentage: number;
  rate: number;
  amortizationPeriod: number;
  loanProgram: LoanProgram;
}

const PRESET_NUMBERS: Record<QuickStartPreset, PresetNumbers> = {
  'first-time': {
    homePrice: 450000,
    downPaymentAmount: 22500,
    downPaymentPercentage: 5,
    rate: 6.75,
    amortizationPeriod: 30,
    loanProgram: 'fha',
  },
  refinance: {
    homePrice: 650000,
    downPaymentAmount: 130000,
    downPaymentPercentage: 20,
    rate: 6.25,
    amortizationPeriod: 15,
    loanProgram: 'conventional',
  },
  investment: {
    homePrice: 1200000,
    downPaymentAmount: 300000,
    downPaymentPercentage: 25,
    rate: 7.25,
    amortizationPeriod: 30,
    loanProgram: 'conventional',
  },
  custom: {
    homePrice: 800000,
    downPaymentAmount: 160000,
    downPaymentPercentage: 20,
    rate: 6.75,
    amortizationPeriod: 30,
    loanProgram: 'conventional',
  },
};

/**
 * Quick-start preset fields for mortgage terms, including country-appropriate loan program and cleared cross-border details.
 */
export function getPresetMortgageTerms(
  preset: QuickStartPreset,
  country: Country
): Partial<MortgageTermsData> {
  const n = PRESET_NUMBERS[preset];

  const usaPmi =
    n.loanProgram === 'conventional' && n.downPaymentPercentage < 20
      ? ({ type: 'percent' as const, value: 0.8 })
      : ({ type: 'percent' as const, value: 0 });

  if (country === 'USA') {
    return {
      country: 'USA',
      homePrice: n.homePrice,
      downPaymentAmount: n.downPaymentAmount,
      downPaymentPercentage: n.downPaymentPercentage,
      rate: n.rate,
      amortizationPeriod: n.amortizationPeriod,
      loanProgram: n.loanProgram,
      pmi: usaPmi,
      canadianTermDetails: undefined,
      cmhcDetails: undefined,
      fhaDetails: undefined,
      vaDetails: undefined,
      usdaDetails: undefined,
    };
  }

  let loanProgram: LoanProgram = 'conventional';
  if (preset === 'first-time') {
    loanProgram = 'cmhc';
  }

  return {
    country: 'Canada',
    homePrice: n.homePrice,
    downPaymentAmount: n.downPaymentAmount,
    downPaymentPercentage: n.downPaymentPercentage,
    rate: n.rate,
    amortizationPeriod: n.amortizationPeriod,
    loanProgram,
    pmi: { type: 'percent', value: 0 },
    canadianTermDetails: { termLength: 5, showRenewalScenario: false, renewalRate: undefined, renewalRates: undefined },
    fhaDetails: undefined,
    vaDetails: undefined,
    usdaDetails: undefined,
    cmhcDetails: undefined,
  };
}

export function getPresetNumbers(preset: QuickStartPreset): PresetNumbers {
  return PRESET_NUMBERS[preset];
}

interface PresetTabsShape {
  titles: [string, string];
  frequencies: [PaymentFrequency, PaymentFrequency];
}

// Descriptive per-tab titles so loading a preset produces a meaningful
// side-by-side comparison (matching the labeled tabs used by article
// scenarios) rather than the generic "CALC 1" / "CALC 2" pair.
const PRESET_TABS: Record<QuickStartPreset, PresetTabsShape> = {
  'first-time': {
    titles: ['First-time buyer', 'First-time bi-weekly'],
    frequencies: ['monthly', 'accelerated-bi-weekly'],
  },
  refinance: {
    titles: ['Refinance 15yr', 'Refinance bi-weekly'],
    frequencies: ['monthly', 'accelerated-bi-weekly'],
  },
  investment: {
    titles: ['Investment 30yr', 'Investment bi-weekly'],
    frequencies: ['monthly', 'accelerated-bi-weekly'],
  },
  custom: {
    titles: ['Custom scenario', 'Custom bi-weekly'],
    frequencies: ['monthly', 'accelerated-bi-weekly'],
  },
};

/**
 * Build the per-tab preset configs applied when a Quick Start pill is clicked.
 *
 * Tab 1 compares against tab 2 on payment frequency (monthly vs accelerated
 * bi-weekly), which is the default out-of-the-box comparison the calculator
 * ships with. Titles describe each tab so renaming is optional.
 */
export function getPresetTabs(
  preset: QuickStartPreset,
  country: Country
): [PresetTabConfig, PresetTabConfig] {
  const base = getPresetMortgageTerms(preset, country);
  const { titles, frequencies } = PRESET_TABS[preset];
  return [
    {
      title: titles[0],
      mortgageTerms: { ...base, paymentFrequency: frequencies[0] },
    },
    {
      title: titles[1],
      mortgageTerms: { ...base, paymentFrequency: frequencies[1] },
    },
  ];
}
